import React, { useEffect, useState } from 'react';
import { View } from "../../src/Components/Themed"
import type { AthleteProps, BestAvailableOrderProps, CompanyProps, EventOrderStatProps, EventProps, LeagueProps, LocationProps, MarketProps, MatchProps, MyPlayerProps, OrderProps, PlayerBalanceProps, PollProps, TeamProps, TournamentProps, TradeProps } from '../../src/types';
import { MarketComponentApi, MarketComponentHelpers } from '../../src/MarketComponents/api';
import { MarketMaketHelpers } from '../../src/MarketMaker/api';
import { FlashMarketApi } from '../../src/FlashMarket/api';
import MarketFilter from './components/MarketFilterCarousel';
import MarketApp from './components/MarketApp';
import OrderHolder from './components/OrderHolder';
import LocationHolder from '../market/components/LocationHolder';
import moment from 'moment-mini';
import { MarketAppHelpers } from './helpers';
import { ActivityIndicator } from 'react-native';
import { useColors } from '../../src/constants/useColors';
import type { BEClarityProps } from '../../src/Clarity';
import BEClarity from '../../src/Clarity';

type MarketEmbedProps = {
    me?:MyPlayerProps,
    my_balance?:PlayerBalanceProps,
    type: 'popular'|'league'|'team',
    league_ids?:string[],
    insets:{ top:number, bottom:number, left:number, right:number },
    team_ids?:string[],
    height?: number,
    default_league_id?:string,
    width?:number
    clarity?:BEClarityProps
    company?:CompanyProps,
    mode:'desktop'|'mobile',
    layout: 'banner'|'card',
    athlete_ids?:string[],
    authenticating?:boolean,
    show_profile?:boolean,
    show_ads?:boolean,
    device_width?:number,
    onNavOut:() => void,
    onShowWallet:() => void,
    onLoaded: () => void,
    onShowProfile:() => void,
    onRequestAuthenticate: () => void,
    onBuyOrder?: (order:OrderProps) => void
}

type StaticDataProps = {
    static_loading:boolean,
    launched:boolean,
    static_loaded:boolean,
    init_event?:EventProps,
    init_tournament?:TournamentProps,
    init_match?:MatchProps,
    nav:'league'|'team'|'athlete',
    init_player_market?:MarketProps,
    markets:MarketProps[],
    leagues:LeagueProps[],
    init_league?:LeagueProps
}

type EmbedDataProps = {
    widget_loading:boolean,
    active_toggle:string,
    flash_markets:PollProps[],
    active_league:string,
    embed_loaded:boolean,
    events:EventProps[],
    active_contest:{ contest_type?:string, contest_id?:string },
    tournaments:TournamentProps[],
    matches:MatchProps[],
    teams:TeamProps[],
    athletes:AthleteProps[],
    best_available_orders:{ [key:string]:BestAvailableOrderProps[] },
    latest_trades:{ [key:string]:TradeProps[] },
    event_order_stats: { [key:string]:EventOrderStatProps[] },
    order_form: { visible:boolean, order?:OrderProps }
}

const MarketEmbed = ({ me, my_balance, clarity, layout, mode, insets, type, default_league_id, company, height, width, league_ids, team_ids, athlete_ids, authenticating, show_profile, show_ads, device_width, onNavOut, onShowWallet, onShowProfile, onLoaded, onRequestAuthenticate, onBuyOrder }:MarketEmbedProps) => {
    const Colors = useColors();
    const [ static_data, setStaticData ] = useState<StaticDataProps>({
        static_loading: false,
        static_loaded:false,
        launched:false,
        leagues: [],
        markets: [],
        nav: 'league'
    });
    const { nav, static_loading, init_match, init_player_market, init_tournament, init_league, static_loaded, launched, leagues, markets, init_event } = static_data;
    const [ embed_data, setEmbedData ] = useState<EmbedDataProps>({
        widget_loading: false,
        active_toggle: 'events',
        active_league: 'Popular',
        teams: [],
        active_contest: {},
        embed_loaded: false,
        flash_markets:[],
        events: [],
        matches: [],
        tournaments: [],
        athletes: [],
        best_available_orders:{},
        event_order_stats: {},
        latest_trades:{},
        order_form: { visible: false }
    });
    const { embed_loaded, widget_loading, active_league, latest_trades, best_available_orders, event_order_stats, events, tournaments, matches, order_form } = embed_data;
    const [ location_state, setLocation ] = useState<{
        visible: boolean,
        show_modal: boolean,
        location?:{
            location: LocationProps,
            region:string,
            coordinates:{ latitude:number, longitude:number, [key:string]:number }
        }
    }>({
        visible:false,
        show_modal:false
    });

    useEffect(() => {
        getStaticData();
    },[]);

    useEffect(() => {
        if(!static_loaded){ return }
        getWidgetData(active_league);
    },[active_league, static_loaded])

    useEffect(() => {
        if(!static_loaded || !embed_loaded){ return }
        onLoaded();
    },[static_loaded, embed_loaded])

    const getStaticData = async() => {
        MarketComponentApi.setEnvironment();
        setStaticData({ ...static_data, static_loading: true });
        const req_leagues = await MarketComponentApi.getLeagues();
        const req_markets = await MarketComponentApi.getMarkets();
        const default_league = req_leagues.find(l => l.league_id == default_league_id);
        if(default_league){ setEmbedData({ ...embed_data, active_league: default_league.league_name }) }
        setStaticData({
            ...static_data,
            leagues: req_leagues,
            markets: req_markets,
            static_loaded: true,
            launched: false,
            static_loading: false
        });
    }

    const getPopularData = async() => {
        let { matches, tournaments, events } = await MarketComponentApi.getBestAvailableOrders();
        let missing_tournaments = MarketComponentHelpers.getMissingTournaments(matches, tournaments);
        tournaments = tournaments.concat(await MarketComponentApi.getTournamentsByTournamentIds(missing_tournaments));
        const fb = MarketComponentHelpers.formatBestAvailable({ events, tournaments, matches });
        const a_ids = athlete_ids ?? []
        let aths:AthleteProps[] = await MarketComponentApi.getAthletesByIds(a_ids);
        const fms = await FlashMarketApi.getActiveMarkets();
        let popular_os = MarketAppHelpers.filterObjByContestIds(fb.order_stats, events.map(e => e.event_id));
        return { flash_markets: fms.polls, events: events, matches:matches, tournaments: tournaments, latest_trades: fb.latest_trades, athletes: aths, event_order_stats: popular_os, best_available_orders: fb.best_available }
    }

    const getLeagueData = async(league_obj:LeagueProps) => {
        let { matches, tournaments, events } = await MarketComponentApi.getBestAvailableOrders();
        const fb = MarketComponentHelpers.formatBestAvailable({ events, tournaments, matches });
        let league_events = events.filter(e => e.league_id == league_obj.league_id);
        const fms = await FlashMarketApi.getActiveMarkets();
        let league_os = MarketAppHelpers.filterObjByContestIds(fb.order_stats, league_events.map(e => e.event_id));
        //Lets just snag the events by date and append the next few upcoming ones just in case we dont have anything
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        let active_league_evs = await MarketComponentApi.getActiveEvents(league_obj.league_id);
        let league_evs = await MarketComponentApi.getEventsByDate(league_obj.league_id, moment().format('YYYY-MM-DD'), timezone);
        
        let additional_evs = MarketAppHelpers.filterNextContests(league_evs.events.concat(active_league_evs), league_evs.day, 20);
        let latest_trades = await MarketComponentApi.getLatestTradesByEvents('team', [ ...new Set(league_events.map(e => e.event_id).concat(additional_evs.map(e => e.event_id))) ]);
        const normalize_resp = MarketAppHelpers.addOrderStatsToEmptyEvents(league_events.concat(additional_evs), league_os, latest_trades);
        return { flash_markets: fms.polls, events: normalize_resp.events, matches:matches, tournaments: tournaments, latest_trades:normalize_resp.latest_trades, athletes: [], event_order_stats: normalize_resp.order_stats, best_available_orders: fb.best_available }
    }

    const getWidgetData = async(league:string) => {
        setEmbedData({ ...embed_data, widget_loading: true });
        MarketComponentApi.setEnvironment();
        FlashMarketApi.setEnvironment()
        //Set up empty objects to fill
        let evs:EventProps[] = []
        let trns: TournamentProps[] = []
        let fms: PollProps[] = []
        let aths:AthleteProps[] = []
        let mtchs:MatchProps[] = []
        let ba:{[key:string]:BestAvailableOrderProps[]} = {}
        let eos:{ [key:string]:EventOrderStatProps[] } = {}
        let lt:{[key:string]:TradeProps[]} = {}
        switch(league){
            case 'Popular':
                const p = await getPopularData();
                evs = p.events
                trns = p.tournaments
                fms = p.flash_markets
                aths = p.athletes
                mtchs = p.matches
                ba = p.best_available_orders
                eos = p.event_order_stats
                lt = p.latest_trades
                break
            default: 
            //this should be a league name... so lets find the league!
                let league_obj = leagues.find(l => l.league_name == league);
                if(!league_obj){ break }
                const l = await getLeagueData(league_obj);
                evs = l.events
                trns = l.tournaments
                fms = l.flash_markets
                aths = l.athletes
                mtchs = l.matches
                ba = l.best_available_orders
                eos = l.event_order_stats
                lt = l.latest_trades
                break
        }

        setEmbedData({
            ...embed_data,
            widget_loading: false,
            embed_loaded: true,
            events: evs,
            matches: mtchs,
            flash_markets: fms,
            tournaments: trns,
            athletes: aths,
            event_order_stats: eos,
            best_available_orders: ba,
            latest_trades: lt
        });
    }

    const renderEmbed = (type:string) => {
        switch(type){
            case 'popular':
                return (
                    <View transparent style={{ height:height, width:width }}>
                        <MarketFilter
                            event_order_stats={event_order_stats}
                            events={events}
                            active_league={active_league}
                            matches={matches}
                            leagues={leagues}
                            markets={markets}
                            insets={insets}
                            mode={mode}
                            layout={layout}
                            authenticated={!!me}
                            show_ads={show_ads}
                            device_width={device_width}
                            onChangeLeague={(league_name) => setEmbedData({ ...embed_data, active_league: league_name })}
                            latest_trades={latest_trades}
                            best_available_orders={best_available_orders}
                            tournaments={tournaments}
                            onBuyOrder={(order) => {
                                console.log('Setting order form with order:', order);
                                if(clarity){ clarity.smartEvent('draft_order') }
                                setEmbedData({ ...embed_data, order_form: { visible: true, order } });
                                if(onBuyOrder) onBuyOrder(order);
                            }}
                            onClaimBonus={() => {
                                onRequestAuthenticate();
                            }}
                            onSelectContest={(contest_type, contest_id, league_id) => {
                                const event = events.find(e => e.event_id == contest_id);
                                const league = leagues.find(l => l.league_id == league_id);
                                if(!event || !league){ return }
                                setStaticData({
                                    ...static_data,
                                    init_event: event,
                                    init_league: league,
                                    launched: true
                                });
                                if(clarity){ clarity?.smartEvent('market_embed_launch') }
                            }}
                            onAdClick={(be_event) => {
                                if(clarity){ clarity?.smartEvent(be_event.event_name) }
                            }}
                        />
                        {widget_loading ?
                        <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'center', alignItems:'center' }}>
                            <ActivityIndicator size={18} color={Colors.text.h1} />
                        </View>
                        :<></>}
                    </View>
                )
            default: return <></>
        }
    }
    return (
        <View transparent style={{ flex:1 }}>
            {renderEmbed(type)}
            <MarketApp
                me={me}
                insets={insets}
                my_balance={my_balance}
                visible={launched}
                leagues={leagues}
                clarity={BEClarity}
                onShowProfile={onShowProfile}
                onShowWallet={onShowWallet}
                company={company}
                init_league={init_league}
                best_available_orders={best_available_orders}
                event_order_stats={event_order_stats}
                markets={markets}
                onNavOut={onNavOut}
                mode={mode}
                init_nav={nav}
                init_event={init_event}
                onRequestAuthenticate={() => {
                    onRequestAuthenticate()
                }}
                onClose={() => setStaticData({ ...static_data, launched: false })}
            />
            {order_form.order ?
            <OrderHolder
                visible={order_form.visible && !location_state.show_modal && !authenticating && !show_profile}
                company_id={company?.company_id}
                order={order_form.order}
                markets={markets}
                event={events.find(e => e.event_id == order_form.order?.event_id)}
                me={me}
                my_balance={my_balance}
                mode={mode}
                location={location_state.location}
                onRequestLocation={() => {
                    setLocation({ ...location_state, visible: true });
                }}
                onRequestAuthenticate={() => {
                    onRequestAuthenticate();
                }}
                onShowProfile={() => {
                    onShowProfile();
                }}
                onClose={() => setEmbedData({ ...embed_data, order_form: { visible: false } })}
                onCreatedOrder={(order) => {
                    console.log('Order created:', order);
                    setEmbedData({ ...embed_data, order_form: { visible: false } });
                    alert('Order placed successfully!');
                }}
            />
            :<></>}
            <LocationHolder
                visible={location_state.visible}
                mode={mode}
                player_id={me?.player_id}
                onClose={() => setLocation({ ...location_state, visible: false, show_modal: false })}
                onModalShow={(showing) => {
                    setLocation({ ...location_state, show_modal: showing });
                }}
                onLocationUpdate={(location) => {
                    if(!location_state.visible){ return }
                    let visible = true
                    if(location?.location?.legal_ind && location_state.visible){ visible = false }
                    setLocation({
                        visible,
                        show_modal: false,
                        location
                    });
                }}
            />
        </View>
    )

}

export default MarketEmbed